import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { cloneDeep } from "lodash";
import {
  Checkbox,
  Grid,
  List,
  Card,
  CardHeader,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Snackbar,
} from "@material-ui/core";
import uuid from "uuid";

import FieldGenerator from "./FieldGenerator";
import { not, union, intersection } from "./utils";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    display: "flex",
    justifyContent: "flex-start",
    "& .MuiSnackbarContent-message": {
      maxHeight: 400,
      overflow: "auto",
    },
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width: 290,
    height: 350,
    backgroundColor: theme.palette.background.paper,
    overflow: "auto",
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
  highlightOnClick: {
    color: theme.palette.primary.main,
  },
  hideHover: {
    "&:hover": {
      background: "unset",
      cursor: "unset",
      transition: "none",
    },
    "& span.MuiTouchRipple-root": {
      top: "unset",
      left: "unset",
      right: "unset",
      bottom: "unset",
      zIndex: "unset",
      overflow: "unset",
      position: "unset",
      borderRadius: "unset",
      pointerEvents: "unset",
    },
  },
}));

export default function TransferList({
  featureList,
  setDataForApply,
  featuresFromRoot,
}) {
  const listTitles = ["Available", "Selected"];
  const classes = useStyles();
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(
    /*
      Filtering by template name. If there is a template on the right side that has been edited
      after adding to the right column, then we need to compare all templates by name
      to display the list correctly.
    */
    featuresFromRoot.length
      ? () => {
          const result = [];
          featureList.forEach((x) => {
            if (
              featuresFromRoot
                .map((y) => Object.keys(y)[0])
                .indexOf(Object.keys(x)[0]) === -1
            ) {
              result.push(x);
            }
          });
          return result;
        }
      : featureList
  );
  const [right, setRight] = useState(featuresFromRoot);
  const [isVisible, setIsVisible] = useState(false);
  const [sideBarData, setSideBarData] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [openSnakeBar, setOpenSnakeBar] = useState(false);
  const [description, setDescription] = useState(false);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => (e) => {
    e.stopPropagation();
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  /*
    If there are elements on the right side, check if the names match the left side
    This must be done, because after changing the template we have on the left side
    a new object is created with the same name but different content.

    If there is a match by the name of the template, we overwrite the old one with the updated element
    from left on the right side
  */

  // featuresFromRoot.length &&
  //   featuresFromRoot.forEach((value, index) => {
  //     const templateName = Object.keys(value)[0];
  //     const itemForUpdate = featureList.find(
  //       (element) => Object.keys(element)[0] === templateName
  //     );

  //     itemForUpdate && featuresFromRoot.splice(index, 1, itemForUpdate);
  //   });
  
  const handleCheckedRight = () => {
    setIsVisible(false);
    setSelectedItem("");
    setSideBarData([]);
    setDataForApply(right.concat(leftChecked));
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setIsVisible(false);
    setSelectedItem("");
    setSideBarData([]);
    setDataForApply(not(right, rightChecked));
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleListItemClick = (featureName, title, value) => {
    if (
      title !== listTitles[1] ||
      (title === listTitles[1] && featureName === selectedItem)
    ) {
      setIsVisible(false);
      setSelectedItem("");
      setSideBarData([]);
    } else {
      setSelectedItem(featureName);
      setSideBarData(idManipulation(cloneDeep(value[featureName]), "inject"));
      setIsVisible(true);
    }
  };

  const idManipulation = (object, action) => {
    const actionId = (object) => {
      if (action === "inject" && !object.id) {
        object.id = uuid();
      }
      if (action === "delete") {
        delete object.id;
      }
      for (const [, value] of Object.entries(object)) {
        if (typeof value === "object") {
          actionId(value);
        }
      }
    };
    actionId(object);
    return object;
  };

  const handleChangeData = (e, objectId) => {
    const { name, value, type } = e.target;

    const defineValueType = () => {
      if (type === "text") {
        return value;
      } else if (type === "number") {
        return Number(value);
      } else {
        return value;
      }
    };

    const findAndUpdate = (object) => {
      const arrayFromObject = Object.entries(object);
      const nodeId = arrayFromObject[arrayFromObject.length - 1][1];

      if (nodeId === objectId) {
        object[name] = defineValueType();
        return;
      } else {
        for (let [, value] of arrayFromObject) {
          if (typeof value === "object") {
            findAndUpdate(value);
          }
        }
      }
    };

    findAndUpdate(sideBarData);

    setSideBarData(sideBarData);
    const modifiedList = right.map((template) => {
      if (!template[selectedItem]) {
        return idManipulation(template, "delete");
      } else {
        return {
          [selectedItem]: idManipulation(cloneDeep(sideBarData), "delete"),
        };
      }
    });

    setRight(modifiedList);
    setDataForApply(modifiedList);
  };

  const showDescription = ({ value, isLeft }) => {
    if (isLeft) {
      const { description, ...restFields } = Object.values(value)[0];

      setOpenSnakeBar(true);
      setDescription(
        <pre>{`Description:\n${
          description?.value || ""
        }\nJSON: ${JSON.stringify(restFields, 0, 1)}`}</pre>
      );
    }
  };

  const closeDescription = (isLeft) => {
    if (isLeft) {
      setOpenSnakeBar(false);
      setDescription("");
    }
  };

  const customList = (title, items, isLeft = false) => {
    return (
      <Card>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Checkbox
              onClick={handleToggleAll(items)}
              checked={
                numberOfChecked(items) === items.length && items.length !== 0
              }
              indeterminate={
                numberOfChecked(items) !== items.length &&
                numberOfChecked(items) !== 0
              }
              disabled={items.length === 0}
              inputProps={{ "aria-label": "all items selected" }}
            />
          }
          title={title}
          subheader={`${numberOfChecked(items)}/${items.length} selected`}
        />
        <Divider />
        <List className={classes.list} dense component="div" role="list">
          {items.map((value) => {
            const featureName = Object.keys(value).toString();
            const labelId = `transfer-list-all-item-${featureName}-label`;

            return (
              <ListItem
                className={
                  title === listTitles[0]
                    ? classes.hideHover
                    : featureName === selectedItem
                    ? classes.highlightOnClick
                    : ""
                }
                key={featureName}
                role="listitem"
                button
                onMouseOver={() => showDescription({ value, isLeft })}
                onClick={() => handleListItemClick(featureName, title, value)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(value) !== -1}
                    onClick={handleToggle(value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={featureName} />
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </Card>
    );
  };

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      className={classes.root}
    >
      <Grid item xs={3}>
        {customList(listTitles[0], left, true)}
      </Grid>
      <Grid item xs={1}>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={3}>
        {customList(listTitles[1], right)}
      </Grid>
      {isVisible && (
        <Grid item xs={5}>
          <FieldGenerator
            selectedItem={selectedItem}
            handleChangeData={handleChangeData}
            sideBarData={sideBarData}
          />
        </Grid>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSnakeBar}
        onClose={() => closeDescription(true)}
        message={description}
        style={{ height: "auto", maxWidth: 600, whiteSpace: "pre-line" }}
      />
    </Grid>
  );
}
